import { ActionIcon, Box, Menu, Text } from '@mantine/core';
import { IconDotsVertical, IconEdit, IconTrash } from '@tabler/icons-react';
import { DataTableColumn } from 'mantine-datatable';
import React, { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { TemplateWithoutSections } from 'src/data-capture/hooks/templates/useTemplates';
import TemplateCreationFlow from './template-creation-flow/TemplateCreationFlow';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import CompanyWrapper from 'src/company/templates/components/CompanyWrapper';
import { useDeleteTemplateMutation } from 'src/company/templates/hooks/useDeleteTemplateMutation';
import TemplateUpdateFlow from './template-creation-flow/TemplateUpdateFlow';

export default function TemplatesListPage() {
  return <CompanyWrapper contentComponent={TemplatesListPageContent} />;
}

function TemplatesListPageContent({ company }: { company: Company }) {
  const [isCreationFlowOpen, setIsCreationFlowOpen] = useState(false);
  const { deleteTemplate } = useDeleteTemplateMutation();

  const [templateToEdit, setTemplateToEdit] = useState<TemplateWithoutSections | null>(null);

  const handleEditTemplate = (template: TemplateWithoutSections) => {
    setTemplateToEdit(template);
  };

  const columns: DataTableColumn<TemplateWithoutSections>[] = [
    {
      accessor: 'displayName',
      title: 'Template name',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.displayName}
        </Text>
      ),
    },
    {
      accessor: 'isActive',
      title: 'Status',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.isActive ? 'Ready to be used' : 'In progress'}
        </Text>
      ),
    },
    {
      accessor: 'createdAt',
      title: 'Created',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.createdAt ? new Date(record.createdAt).toLocaleDateString() : null}
        </Text>
      ),
    },
    {
      accessor: 'actions',
      title: '',
      render: record => (
        <Box ta="right">
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                onClick={() => handleEditTemplate(record)}
              >
                Edit template
              </Menu.Item>
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => deleteTemplate(company.id, record.id)}
              >
                Delete template
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      ),
    },
  ];

  const handleAddTemplate = () => {
    setIsCreationFlowOpen(true);
  };

  const handleCloseCreationFlow = () => {
    setIsCreationFlowOpen(false);
  };

  return (
    <>
      <SimpleTable<TemplateWithoutSections>
        resource={`admin/companies/${company.id}/templates`}
        filters={{ templateType: 'SUITABILITY_REPORT' }}
        columns={columns}
        title="Templates"
        primaryButton={{
          label: 'Add template',
          onClick: handleAddTemplate,
          variant: 'primary',
        }}
        showCount
        enableSearch
        pageSize={10}
        mode="client"
      />

      <TemplateCreationFlow
        company={company}
        opened={isCreationFlowOpen}
        close={handleCloseCreationFlow}
      />
      {templateToEdit && (
        <TemplateUpdateFlow
          templateId={templateToEdit.id}
          company={company}
          opened={!!templateToEdit}
          close={() => setTemplateToEdit(null)}
        />
      )}
    </>
  );
}
